import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Users, Lock, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [contactInquiries, setContactInquiries] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [activeTab, setActiveTab] = useState('contact');
  const [error, setError] = useState('');

  // Simple password - you can change this
  const ADMIN_PASSWORD = 'ServantCEO2025';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchData();
    } else {
      setError('Incorrect password');
    }
  };

  const fetchData = async () => {
    try {
      const [contactRes, newsletterRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/newsletter/subscribers`)
      ]);
      setContactInquiries(contactRes.data);
      setNewsletterSubscribers(newsletterRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div data-testid="admin-page" className="min-h-screen flex items-center justify-center pt-20 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass-card rounded-2xl p-8 w-full max-w-md"
        >
          <div className="flex items-center justify-center mb-6">
            <Lock className="text-accent mr-3" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-center mb-6">Admin Login</h1>
          
          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent transition-all"
                placeholder="Enter admin password"
                required
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-destructive text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold mb-8">Admin Dashboard</h1>

          {/* Tabs */}
          <div className="flex space-x-4 mb-8 border-b border-border">
            <button
              onClick={() => setActiveTab('contact')}
              data-testid="tab-contact"
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'contact'
                  ? 'border-b-2 border-accent text-accent'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Mail className="inline mr-2" size={20} />
              Contact Inquiries ({contactInquiries.length})
            </button>
            <button
              onClick={() => setActiveTab('newsletter')}
              data-testid="tab-newsletter"
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'newsletter'
                  ? 'border-b-2 border-accent text-accent'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Users className="inline mr-2" size={20} />
              Newsletter Subscribers ({newsletterSubscribers.length})
            </button>
          </div>

          {/* Contact Inquiries */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              {contactInquiries.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">No contact inquiries yet.</p>
              ) : (
                contactInquiries.map((inquiry, index) => (
                  <motion.div
                    key={inquiry.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    data-testid={`inquiry-${index}`}
                    className="glass-card rounded-xl p-6"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold">{inquiry.name}</h3>
                        <a href={`mailto:${inquiry.email}`} className="text-accent hover:underline">
                          {inquiry.email}
                        </a>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(inquiry.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="inline-block px-3 py-1 bg-accent/20 text-accent rounded-full text-sm">
                        {inquiry.inquiry_type}
                      </span>
                      {inquiry.company && (
                        <span className="ml-2 text-sm text-muted-foreground">
                          Company: {inquiry.company}
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground whitespace-pre-wrap">{inquiry.message}</p>
                  </motion.div>
                ))
              )}
            </div>
          )}

          {/* Newsletter Subscribers */}
          {activeTab === 'newsletter' && (
            <div className="space-y-4">
              {newsletterSubscribers.length === 0 ? (
                <p className="text-muted-foreground text-center py-12">No newsletter subscribers yet.</p>
              ) : (
                <div className="glass-card rounded-xl p-6">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4">Name</th>
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-left py-3 px-4">Subscribed Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterSubscribers.map((subscriber, index) => (
                        <tr key={subscriber.id} className="border-b border-border last:border-0" data-testid={`subscriber-${index}`}>
                          <td className="py-3 px-4">{subscriber.name || 'N/A'}</td>
                          <td className="py-3 px-4">
                            <a href={`mailto:${subscriber.email}`} className="text-accent hover:underline">
                              {subscriber.email}
                            </a>
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">
                            {new Date(subscriber.timestamp).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage;
